import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowsClockwise, FolderOpen } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (v, currency) =>
  v === null || v === undefined ? "—" : `${currency === "INR" ? "₹" : `${currency} `}${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const qty = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

// What NPD booked in a month, SAP project -> task -> material, valued at SAP's
// moving average price (user's ask, Oct 19 2026: "so NPD spend is visible by
// project").
export const NpdProjectSpendTab = ({ sites = [] }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [siteId, setSiteId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openProject, setOpenProject] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axios.get(`${API}/npd-store-requests/project-spend`, {
        params: { month, ...(siteId !== "all" ? { site_id: siteId } : {}) },
      });
      setData(res);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load project spend");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month, siteId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-[#EAECF0] rounded-sm" data-testid="npd-project-spend-tab">
      <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-[#EAECF0]">
        <div className="space-y-1">
          <Label className="text-xs font-bold text-[#344054]">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="npd-spend-month" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold text-[#344054]">Site</Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="h-9 w-32" data-testid="npd-spend-site"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="npd-spend-site-all">All sites</SelectItem>
              {sites.map((s) => <SelectItem key={s} value={s} data-testid={`npd-spend-site-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="npd-spend-refresh">
          <ArrowsClockwise size={14} className="mr-1.5" />{loading ? "Loading..." : "Refresh"}
        </Button>
        <div className="ml-auto flex items-end gap-6 text-right">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-[#667085]">Material booked this month</p>
            <p className="text-xl font-bold text-[#101828]" data-testid="npd-spend-total">
              {data ? money(data.total_value, data.currency) : "\u2014"}
            </p>
          </div>
          {/* Jun 2026, user's ask: tooling budgets tracked per project, in the
              app's own numbers. All-time on purpose - a budget is sanctioned
              once and spent over however long the tool takes. */}
          <div>
            <p className="text-[11px] uppercase tracking-wide text-[#667085]">Tooling sanctioned (all time)</p>
            <p className="text-xl font-bold text-[#027A48]" data-testid="npd-tooling-grand-sanctioned">
              {data ? money(data.tooling_totals?.sanctioned, data.currency) : "\u2014"}
            </p>
            {data?.tooling_totals?.awaiting > 0 && (
              <p className="text-[11px] text-[#B54708]" data-testid="npd-tooling-grand-awaiting">
                {money(data.tooling_totals.awaiting, data.currency)} awaiting approval
              </p>
            )}
          </div>
        </div>
      </div>

      {error && <p className="px-4 py-3 text-[13px] text-[#B42318]" data-testid="npd-spend-error">{error}</p>}

      {data && data.pending_postings > 0 && (
        <p className="px-4 py-2 text-[12px] text-[#B54708] bg-[#FFFAEB] border-b border-[#FEDF89]" data-testid="npd-spend-pending">
          {data.pending_postings} posting(s) this month have not finished in SAP yet and are not counted above.
        </p>
      )}

      {data && data.projects.length === 0 && !loading && (
        <p className="px-4 py-10 text-center text-[13px] text-[#667085]" data-testid="npd-spend-empty">
          Nothing was consumed in {data.month}{siteId !== "all" ? ` at ${siteId}` : ""}.
        </p>
      )}

      <div className="divide-y divide-[#EAECF0]">
        {(data?.projects || []).map((p) => {
          const key = p.group_key || p.project_id || p.project_name;
          const open = openProject === key;
          const testKey = p.project_id || (p.legacy_ref ? "legacy" : "no-project");
          return (
            <div key={key} data-testid={`npd-spend-project-${testKey}`}>
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F9FAFB]"
                onClick={() => setOpenProject(open ? null : key)}
                data-testid={`npd-spend-project-toggle-${testKey}`}
              >
                <FolderOpen size={16} className="text-[#0E9384]" />
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-[#101828] truncate">
                    {p.project_id ? <span className="font-mono mr-1.5">{p.project_id}</span> : null}
                    {p.project_name || "No project"}
                    {!p.project_id && (
                      <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wide text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-1.5 py-0.5">
                        {p.legacy_ref ? "Before SAP projects" : "No project"}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-[#667085]">
                    {p.tasks.length} task(s) · {p.postings} posting(s){p.project_status ? ` · ${p.project_status}` : ""}
                    {!p.project_id && (p.legacy_ref
                      ? " · booked before SAP projects were the source, so it has no SAP project or task"
                      : " · booked to the NPD cost centre, not to a project")}
                  </p>
                </div>
                <span className="ml-auto text-right">
                  <span className="block text-[14px] font-bold text-[#101828]">{money(p.value, data.currency)}</span>
                  {p.tooling && (p.tooling.sanctioned > 0 || p.tooling.awaiting > 0) && (
                    <span className="block text-[11px] text-[#667085]" data-testid={`npd-tooling-chip-${testKey}`}>
                      Tooling: <strong className="text-[#027A48]">{money(p.tooling.sanctioned, data.currency)}</strong>
                      {p.tooling.awaiting > 0 ? <> &middot; <span className="text-[#B54708]">{money(p.tooling.awaiting, data.currency)} pending</span></> : null}
                    </span>
                  )}
                </span>
              </button>

              {open && (
                <div className="px-4 pb-4 space-y-3">
                  {p.tooling && (
                    <div className="border border-[#D0D5DD] rounded-sm" data-testid={`npd-tooling-block-${testKey}`}>
                      <div className="flex flex-wrap items-center gap-4 px-3 py-2 bg-[#F9FAFB] border-b border-[#EAECF0]">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-[#344054]">Tooling budgets (all time)</span>
                        <span className="text-[11px] text-[#667085]">Requested <strong className="text-[#101828]">{money(p.tooling.requested, data.currency)}</strong></span>
                        <span className="text-[11px] text-[#667085]">Awaiting approval <strong className="text-[#B54708]">{money(p.tooling.awaiting, data.currency)}</strong></span>
                        <span className="text-[11px] text-[#667085]">Sanctioned <strong className="text-[#027A48]">{money(p.tooling.sanctioned, data.currency)}</strong></span>
                        {p.tooling.counts?.rejected > 0 && (
                          <span className="text-[11px] text-[#98A2B3]">{p.tooling.counts.rejected} rejected</span>
                        )}
                      </div>
                      <table className="w-full text-[12px]">
                        <thead className="text-[#667085]">
                          <tr className="border-b border-[#EAECF0]">
                            <th className="text-left font-bold px-3 py-1.5">Request</th>
                            <th className="text-left font-bold px-3 py-1.5">Task</th>
                            <th className="text-left font-bold px-3 py-1.5">Raised by</th>
                            <th className="text-right font-bold px-3 py-1.5">Requested</th>
                            <th className="text-right font-bold px-3 py-1.5">Sanctioned</th>
                            <th className="text-left font-bold px-3 py-1.5">Stage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.tooling.requests.map((r) => (
                            <tr key={r._id} className="border-t border-[#F2F4F7]" data-testid={`npd-tooling-project-row-${r._id}`}>
                              <td className="px-3 py-1.5 font-mono">{r._id}</td>
                              <td className="px-3 py-1.5">{r.task_id || "\u2014"}</td>
                              <td className="px-3 py-1.5 text-[#667085]">{r.requester}</td>
                              <td className="px-3 py-1.5 text-right">{money(r.amount_requested, data.currency)}</td>
                              <td className="px-3 py-1.5 text-right font-bold text-[#027A48]">{r.approved_amount != null ? money(r.approved_amount, data.currency) : "\u2014"}</td>
                              <td className="px-3 py-1.5 text-[#667085]">
                                {r.status === "approved" ? (r.in_sap ? "Approved \u00B7 in SAP" : "Approved \u00B7 not in SAP")
                                  : r.status === "rejected" ? "Rejected" : `Awaiting ${r.stage_label}`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {p.tasks.map((t) => (
                    <div key={t.task_id || "none"} className="border border-[#EAECF0] rounded-sm" data-testid={`npd-spend-task-${t.task_id || "none"}`}>
                      <div className="flex items-center gap-2 px-3 py-2 bg-[#F9FAFB] border-b border-[#EAECF0]">
                        {t.task_id
                          ? <span className="font-mono text-[11px] font-bold text-[#0E9384]">{t.task_id}</span>
                          : <span className="text-[12px] text-[#667085]">No SAP task — booked to the NPD cost centre</span>}
                        <span className="text-[12px] text-[#344054]">{t.task_name || ""}</span>
                        {t.phase_name && <span className="text-[10px] text-[#98A2B3]">· {t.phase_name}</span>}
                        <span className="ml-auto text-[12px] font-bold">{money(t.value, data.currency)}</span>
                      </div>
                      <table className="w-full text-[12px]">
                        <thead className="text-[#667085]">
                          <tr>
                            <th className="text-left font-bold px-3 py-1.5">Material</th>
                            <th className="text-right font-bold px-3 py-1.5">Qty</th>
                            <th className="text-right font-bold px-3 py-1.5">Rate</th>
                            <th className="text-right font-bold px-3 py-1.5">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {t.materials.map((m) => (
                            <tr key={m.product_id} className="border-t border-[#F2F4F7]" data-testid={`npd-spend-material-${m.product_id}`}>
                              <td className="px-3 py-1.5">
                                <span className="font-mono">{m.product_id}</span>
                                <span className="text-[#667085]"> · {m.description || "—"}</span>
                              </td>
                              <td className="px-3 py-1.5 text-right">{qty(m.qty)} {m.uom}</td>
                              <td className="px-3 py-1.5 text-right">{m.valued ? money(m.rate, data.currency) : "no price"}</td>
                              <td className="px-3 py-1.5 text-right font-bold">{m.valued ? money(m.value, data.currency) : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data?.unvalued?.length > 0 && (
        <p className="px-4 py-2 text-[11px] text-[#667085] border-t border-[#EAECF0]" data-testid="npd-spend-unvalued">
          No SAP price found for: {data.unvalued.join(", ")} — their quantity is counted, their value is not.
        </p>
      )}

      {data?.zero_valued?.length > 0 && (
        <p className="px-4 py-2 text-[11px] text-[#667085] border-t border-[#EAECF0]" data-testid="npd-spend-zero-valued">
          SAP holds {data.zero_valued.join(", ")} at zero value (scrap or free issue), so they add quantity but no spend.
        </p>
      )}
    </div>
  );
};
